/**
 * Free AI image upscaler — super-resolution in the visitor's browser.
 *
 * TensorFlow.js + UpscalerJS (both MIT) with the ESRGAN "slim" (fast) and "medium" (best) models,
 * fetched from jsdelivr the first time a file is dropped, so the page itself stays light and the
 * artwork never leaves the tab. Inputs are shrunk to 1024px on the long edge first (bigger files do
 * not need upscaling) and results stop at 4096px, so a phone can finish the job. Transparent PNGs
 * stay transparent: the colour is upscaled by the network, the alpha by a smooth resize.
 *
 * Hand-offs (IndexedDB mcp-tools / handoff, the same store the other tools use):
 *   key "upscale" — a small file sent here by the patch builder ({ blob, name, at, returnTo });
 *   key "cutout"  — the finished image handed to the builder ({ blob, name, at, from }).
 */
(() => {
  const CDN = {
    tf: 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.11.0/dist/tf.min.js',
    upscaler: 'https://cdn.jsdelivr.net/npm/upscaler@1.0.0/dist/browser/umd/upscaler.min.js',
    models: {
      'slim-2': { src: 'https://cdn.jsdelivr.net/npm/@upscalerjs/esrgan-slim@1.0.0/dist/umd/models/esrgan-slim/src/x2/index.min.js', global: 'ESRGANSlim2x' },
      'slim-4': { src: 'https://cdn.jsdelivr.net/npm/@upscalerjs/esrgan-slim@1.0.0/dist/umd/models/esrgan-slim/src/x4/index.min.js', global: 'ESRGANSlim4x' },
      'medium-2': { src: 'https://cdn.jsdelivr.net/npm/@upscalerjs/esrgan-medium@1.0.0/dist/umd/models/esrgan-medium/src/x2/index.min.js', global: 'ESRGANMedium2x' },
      'medium-4': { src: 'https://cdn.jsdelivr.net/npm/@upscalerjs/esrgan-medium@1.0.0/dist/umd/models/esrgan-medium/src/x4/index.min.js', global: 'ESRGANMedium4x' },
    },
  };
  const HANDOFF = { db: 'mcp-tools', store: 'handoff', out: 'cutout', in: 'upscale' };
  const MAX_IN = 1024, MAX_OUT = 4096, FRESH = 30 * 60 * 1000;

  /* ───────────── scripts and models, loaded once, on demand ───────────── */
  const loading = new Map();
  const load = (src) => {
    if (!loading.has(src)) loading.set(src, new Promise((resolve, reject) => {
      const s = document.createElement('script'); s.src = src; s.async = true;
      s.onload = () => resolve(); s.onerror = () => { loading.delete(src); reject(new Error('Could not load ' + src)); };
      document.head.appendChild(s);
    }));
    return loading.get(src);
  };
  const engines = new Map();
  async function engine(key) {
    await load(CDN.tf); await load(CDN.upscaler);
    const m = CDN.models[key]; await load(m.src);
    await window.tf.ready();
    const def = window[m.global] || window[Object.keys(window).find((k) => k.toLowerCase() === m.global.toLowerCase())];
    if (!def) throw new Error('Model missing: ' + m.global);
    if (!engines.has(key)) engines.set(key, new window.Upscaler({ model: def }));
    return engines.get(key);
  }

  /* ───────────────────────────── hand-off ───────────────────────────── */
  function openStore() {
    return new Promise((resolve, reject) => {
      const open = indexedDB.open(HANDOFF.db, 1);
      open.onupgradeneeded = () => { try { open.result.createObjectStore(HANDOFF.store); } catch (e) { /* exists */ } };
      open.onerror = () => reject(open.error);
      open.onsuccess = () => resolve(open.result);
    });
  }
  async function put(key, record) {
    const db = await openStore();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(HANDOFF.store, 'readwrite');
      tx.objectStore(HANDOFF.store).put({ ...record, at: Date.now() }, key);
      tx.oncomplete = resolve; tx.onerror = () => reject(tx.error);
    });
    db.close();
  }
  async function take(key) {
    const db = await openStore();
    if (!db.objectStoreNames.contains(HANDOFF.store)) { db.close(); return null; }
    const held = await new Promise((resolve) => {
      const tx = db.transaction(HANDOFF.store, 'readwrite');
      const store = tx.objectStore(HANDOFF.store);
      const read = store.get(key);
      read.onsuccess = () => { const v = read.result; store.delete(key); resolve(v || null); };
      read.onerror = () => resolve(null);
    });
    db.close();
    return held && held.blob && Date.now() - held.at < FRESH ? held : null;
  }

  /* ───────────────────────────── the page ───────────────────────────── */
  const make = (w, h) => { const c = document.createElement('canvas'); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; };

  function init(root) {
    const q = (s) => root.querySelector(s);
    const drop = q('[data-up-drop]'), fileInput = q('[data-up-file]'), before = q('[data-up-before]'), canvas = q('[data-up-canvas]');
    const progress = q('[data-up-progress]'), bar = q('[data-up-bar]'), pct = q('[data-up-pct]');
    const status = q('[data-up-status]'), nameEl = q('[data-up-name]');
    const download = q('[data-up-download]'), use = q('[data-up-use]'), cancel = q('[data-up-cancel]'), reset = q('[data-up-reset]');
    const compare = q('[data-up-compare]');
    if (!fileInput || !canvas) return;
    root.dataset.upReady = '1';
    const text = (k) => root.dataset[k] || '';

    let source = null; // HTMLImageElement of the picked file
    let name = 'artwork', returnTo = '', run = 0, busy = null, outputBlobUrl = null;
    const say = (m, bad) => { if (status) { status.textContent = m || ''; status.classList.toggle('is-error', !!bad); } };
    const pick = (sel) => (root.querySelector(sel + ':checked') || {}).value;
    const setProgress = (rate) => { if (!progress) return; progress.hidden = rate == null; if (rate != null) { const p = Math.round(rate * 100); bar.style.width = p + '%'; pct.textContent = p + '%'; } };

    /** Scale the picked image down to the working size; returns a canvas plus its alpha, if any. */
    function working() {
      const long = Math.max(source.naturalWidth, source.naturalHeight);
      const s = Math.min(1, MAX_IN / long);
      const w = Math.round(source.naturalWidth * s), h = Math.round(source.naturalHeight * s);
      const c = make(w, h); const cx = c.getContext('2d', { willReadFrequently: true }); cx.imageSmoothingQuality = 'high';
      cx.drawImage(source, 0, 0, w, h);
      const data = cx.getImageData(0, 0, w, h).data;
      let transparent = false;
      for (let i = 3; i < data.length; i += 4) if (data[i] < 250) { transparent = true; break; }
      // The network sees RGB only: flatten onto white so edges do not pick up black.
      const rgb = make(w, h); const rc = rgb.getContext('2d'); rc.fillStyle = '#FFFFFF'; rc.fillRect(0, 0, w, h); rc.drawImage(c, 0, 0);
      return { rgb, alphaSource: transparent ? c : null, w, h, shrunk: s < 1 };
    }

    async function upscale() {
      if (!source) return;
      const my = ++run;
      let scale = Number(pick('[data-up-scale]')) || 4;
      const quality = pick('[data-up-quality]') || 'slim';
      const w0 = working();
      if (Math.max(w0.w, w0.h) * scale > MAX_OUT) scale = 2;
      const key = `${quality}-${scale}`;
      say(text('upLoading')); setProgress(0);
      if (cancel) cancel.hidden = false; if (download) download.disabled = true; if (use) use.setAttribute('aria-disabled', 'true');
      try {
        const up = await engine(key);
        if (my !== run) return;
        busy = up;
        const started = performance.now();
        const tensor = await up.upscale(w0.rgb, { output: 'tensor', patchSize: 64, padding: 2, progress: (rate) => { if (my === run) { setProgress(rate); say(text('upWorking')); } } });
        if (my !== run) { tensor.dispose(); return; }
        const [oh, ow] = tensor.shape;
        // The ESRGAN models hand back float pixels in 0–255 (some in 0–1); toPixels wants int 0–255.
        const peak = (await tensor.max().data())[0];
        const ints = window.tf.tidy(() => (peak <= 1 ? tensor.mul(255) : tensor).clipByValue(0, 255).round().toInt());
        tensor.dispose();
        const pixels = await window.tf.browser.toPixels(ints); ints.dispose();
        const out = new ImageData(new Uint8ClampedArray(pixels), ow, oh);
        if (w0.alphaSource) {
          // Alpha by a smooth resize of the original, so transparent files stay transparent.
          const a = make(ow, oh); const ac = a.getContext('2d', { willReadFrequently: true }); ac.imageSmoothingQuality = 'high';
          ac.drawImage(w0.alphaSource, 0, 0, ow, oh);
          const ad = ac.getImageData(0, 0, ow, oh).data;
          for (let i = 3; i < out.data.length; i += 4) out.data[i] = ad[i];
        }
        canvas.width = ow; canvas.height = oh; canvas.getContext('2d').putImageData(out, 0, 0);
        root.classList.add('is-done');
        const secs = Math.max(1, Math.round((performance.now() - started) / 1000));
        say(text('upDone').replace('[from]', `${w0.w} × ${w0.h}`).replace('[to]', `${ow} × ${oh}`).replace('[secs]', String(secs)) + (w0.shrunk ? ' ' + text('upShrunk') : ''));
        if (download) download.disabled = false; if (use) use.removeAttribute('aria-disabled');
      } catch (e) {
        if (my !== run) return;
        say((/abort/i.test(String(e && e.message)) ? text('upStopped') : text('upFailed')), true);
      } finally {
        if (my === run) { setProgress(null); if (cancel) cancel.hidden = true; busy = null; }
      }
    }

    function loadFile(file) {
      if (!file || !/^image\/(png|jpe?g|webp|gif|bmp)$/i.test(file.type)) { say(text('upBadFile'), true); return; }
      name = (file.name || 'artwork').replace(/\.[^.]+$/, '') || 'artwork';
      if (nameEl) nameEl.textContent = file.name || '';
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { source = img; if (before) before.src = url; root.classList.add('is-ready'); root.classList.remove('is-done'); upscale(); };
      img.onerror = () => { URL.revokeObjectURL(url); say(text('upBadFile'), true); };
      img.src = url;
    }
    const output = () => new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

    fileInput.addEventListener('change', () => loadFile(fileInput.files && fileInput.files[0]));
    if (drop) {
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-over'); }));
      drop.addEventListener('drop', (e) => loadFile(e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]));
    }
    document.addEventListener('paste', (e) => { const item = [...((e.clipboardData && e.clipboardData.items) || [])].find((i) => /^image\//.test(i.type)); if (item) loadFile(item.getAsFile()); });
    root.querySelectorAll('[data-up-scale], [data-up-quality]').forEach((el) => el.addEventListener('change', () => { if (source) upscale(); }));
    if (cancel) cancel.addEventListener('click', () => { run++; if (busy && busy.abort) { try { busy.abort(); } catch (e) { /* fine */ } } setProgress(null); cancel.hidden = true; say(text('upStopped')); });
    if (reset) reset.addEventListener('click', () => { run++; source = null; root.classList.remove('is-ready', 'is-done'); fileInput.value = ''; say(''); setProgress(null); });
    if (download) download.addEventListener('click', async () => {
      const blob = await output(); if (!blob) return;
      if (outputBlobUrl) URL.revokeObjectURL(outputBlobUrl);
      outputBlobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = outputBlobUrl; a.download = `${name}-${canvas.width}px.png`; document.body.appendChild(a); a.click(); a.remove();
    });
    if (use) use.addEventListener('click', async (e) => {
      e.preventDefault();
      if (use.getAttribute('aria-disabled') === 'true') return;
      const blob = await output(); if (!blob) return;
      say(text('upHanding'));
      try { await put(HANDOFF.out, { blob, name: `${name}-upscaled.png`, from: 'upscaler' }); } catch (err) { /* still go */ }
      window.location.href = returnTo || use.getAttribute('href');
    });
    if (compare) compare.addEventListener('change', () => root.classList.toggle('is-compare', compare.checked));

    // A small file sent over from the patch builder: load it straight away and offer the way back.
    take(HANDOFF.in).then((held) => {
      if (!held) return;
      returnTo = held.returnTo || '';
      if (use && returnTo && text('upReturn')) use.textContent = text('upReturn');
      loadFile(new File([held.blob], held.name || 'artwork.png', { type: held.blob.type || 'image/png' }));
    }).catch(() => {});
  }

  const boot = () => document.querySelectorAll('[data-image-upscaler]:not([data-up-ready])').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
